/**
 * The route switch: `/playChess` and `/CCTournaments` render THIS, and it
 * decides — per tab, per request — whether the old PlayChess screen or the new
 * PlayChessV2 one serves it. Route names, URLs and call sites are unchanged.
 *
 * With `PLAY_V2_COHORT: 'off'` — or 'flag' for a user without the `play_v2`
 * flag — `resolveLane()` returns 'old' synchronously, so nothing about today's
 * screen changes for them (not even a frame of shimmer).
 */
import React, {useEffect, useState} from 'react';
import {View} from 'react-native';

import PlayChess from '@/Screens/PlayChess';
import ShimmerLoader from '@/Screens/PlayChess/components/ShimmerLoader';
import PlayChessV2 from '@/Screens/PlayChessV2';
import {peekHandshakes} from '@/Store/liveGame/handshakes';
import {liveGameStore} from '@/Store/liveGameStore';
import {wereV2HandlersRegistered} from '@/Screens/PlayChessV2/hooks/useGameplayHandlers';

import LaneToggle from './LaneToggle';
import {canSwitchLane, lockLane, resolveLane} from './playFlags';
import {resolveFlow, SUPPORTED_FLOWS} from './resolveFlow';

type Decision = 'pending' | 'old' | 'new';

function PlayChessSwitch(props: any) {
  const isStaff = Boolean(
    props?.data?.data?.user?.[0]?.fields?.is_staff ??
      props?.dashboardData?.user?.[0]?.fields?.is_staff,
  );
  // fix AE: the per-user flags ride the same profile payload (`user_flags`,
  // keyed by `cc_control_flags.flag_key`); the route gets it as `data` and
  // the dashboard wrapper as `dashboardData.data`.
  const userFlags =
    props?.data?.data?.user_flags ??
    props?.dashboardData?.data?.user_flags ??
    props?.dashboardData?.user_flags ??
    null;
  // Read once: the lane must not flip mid-session because a prop arrived late.
  const [audience] = useState(() => ({isStaff, userFlags}));
  const [lane] = useState(() => resolveLane(audience));
  const [eligible] = useState(() => canSwitchLane(audience));
  const [decision, setDecision] = useState<Decision>(() =>
    lane === 'old' ? 'old' : 'pending',
  );

  useEffect(() => {
    if (lane === 'old') {
      return;
    }
    let cancelled = false;
    (async () => {
      // peekHandshakes is async (localForage); the flow decision waits for it,
      // which is why the new lane shows a shimmer for one tick.
      const handshakes = await peekHandshakes();
      if (cancelled) {
        return;
      }
      const {lane: resolved} = resolveFlow({
        params: props?.route?.params,
        handshakes,
        storeSnapshot: liveGameStore.getState(),
        supported: SUPPORTED_FLOWS,
      });
      lockLane(resolved);
      if (resolved === 'old' && wereV2HandlersRegistered()) {
        // The SDK cannot unregister handlers, so a tab that already wired the
        // V2 lane has to start clean before the old screen takes over.
        window.location.reload();
        return;
      }
      setDecision(resolved);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lane]);

  if (decision === 'pending') {
    return (
      <ShimmerLoader isLoading={true}>
        <View />
      </ShimmerLoader>
    );
  }

  const screen =
    decision === 'new' ? <PlayChessV2 {...props} /> : <PlayChess {...props} />;

  // The lane badge is for people INSIDE the rollout (fix AE: `canSwitchLane`
  // — staff, a flagged user, or everyone once the cohort is 'all'); nobody
  // else sees that a second lane exists.
  return (
    <>
      {screen}
      {eligible && <LaneToggle lane={decision} />}
    </>
  );
}

export default PlayChessSwitch;
